import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    addNewProduct,
    deleteProduct as deleteProductApi,
    getProducts,
    ProductPayload,
    updateProduct as updateProductApi
} from "../../services/productService";

export const initialState = {
    products: [],
    loading: false,
    error: null,
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
};

export const fetchProducts = createAsyncThunk(
    "products/fetchProducts",
    async ({ pageNumber, pageSize }: { pageNumber: number; pageSize: number }, { rejectWithValue }) => {
        try {
            const response = await getProducts(pageNumber, pageSize);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const addProduct = createAsyncThunk(
    "products/addProduct",
    async (product: ProductPayload, { rejectWithValue }) => {
        try {
            const response = await addNewProduct(product);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const updateProduct = createAsyncThunk(
    "products/updateProduct",
    async ({ id, data }: { id: string; data: ProductPayload }, { rejectWithValue }) => {
        try {
            const response = await updateProductApi(id, data);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const deleteProduct = createAsyncThunk(
    "products/deleteProduct",
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteProductApi(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

const productSlice = createSlice({
    name: "products",
    initialState,
    reducers: {
        selectProduct: (state, action) => {
            // Optional: logic to select a product
        }
    },
    extraReducers: (builder) => {
        // Fetch
        builder.addCase(fetchProducts.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchProducts.fulfilled, (state: any, action: any) => {
            state.loading = false;
            // Handle pagination response structure
            let products = [];
            if (action.payload && action.payload.items) {
                products = action.payload.items;
                state.totalCount = action.payload.totalCount;
                state.totalPages = action.payload.totalPages;
                state.currentPage = action.payload.pageNumber;
            } else if (Array.isArray(action.payload)) {
                products = action.payload;
            }

            // Map API response: unitPrice -> defaultRentalRate
            state.products = products.map((product: any) => ({
                ...product,
                defaultRentalRate: product.unitPrice !== undefined ? product.unitPrice : product.defaultRentalRate
            }));
        });
        builder.addCase(fetchProducts.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch products";
        });

        // Add
        builder.addCase(addProduct.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addProduct.fulfilled, (state: any, action: any) => {
            state.loading = false;
            // Handle response - could be wrapped in data property or direct
            const product = action.payload?.data || action.payload;
            if (product && product.id) {
                // Map API response: unitPrice -> defaultRentalRate
                const mappedProduct = {
                    ...product,
                    defaultRentalRate: product.unitPrice !== undefined ? product.unitPrice : product.defaultRentalRate
                };
                // Check if product already exists (avoid duplicates)
                const exists = state.products.some((p: any) => p.id === mappedProduct.id);
                if (!exists) {
                    state.products.push(mappedProduct);
                    state.totalCount = (state.totalCount || 0) + 1;
                }
            }
        });
        builder.addCase(addProduct.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload?.message || action.payload || "Failed to add product";
        });

        // Update
        builder.addCase(updateProduct.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateProduct.fulfilled, (state: any, action: any) => {
            state.loading = false;
            const product = action.payload?.data || action.payload;
            // Map API response: unitPrice -> defaultRentalRate
            const mappedProduct = {
                ...product,
                defaultRentalRate: product?.unitPrice !== undefined ? product.unitPrice : product?.defaultRentalRate
            };
            const index = state.products.findIndex((p: any) => p.id === mappedProduct.id);
            if (index !== -1) {
                state.products[index] = mappedProduct;
            }
        });
        builder.addCase(updateProduct.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to update product";
        });

        // Delete
        builder.addCase(deleteProduct.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteProduct.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.products = state.products.filter((p: any) => p.id !== action.payload);
        });
        builder.addCase(deleteProduct.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to delete product";
        });
    },
});

export const { selectProduct } = productSlice.actions;

export const selectProductList = (state: any) => state.Products.products;
export const selectProductById = (state: any, id: string) =>
    state.Products.products.find((p: any) => p.id === id);
export const selectProductLoading = (state: any) => state.Products.loading;
export const selectProductError = (state: any) => state.Products.error;

export default productSlice.reducer;

